/**
 * Weekly hours, closed dates, and after-hours behavior.
 * Props: api (API.json), toast, timezone.
 * Saves PATCH /api/tenant/settings. Hours JSON: { weekly: { mon: { start, end } }, holidays: ['YYYY-MM-DD' | { date, name }] }.
 * A missing day is closed. No weekly object means the AI answers all the time.
 */
const { useState, useEffect } = React;

const DAYS = [
  ['sun', 'Sunday'],
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday']
];

const WEEKDAYS = ['tue', 'wed', 'thu', 'fri'];

const AFTER_HOURS = [
  ['voicemail', 'Take a message'],
  ['callback', 'Take a message and call them back'],
  ['message', 'Tell them we are closed']
];

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.detail || data.message)) || (err && err.message) || fallback;
}

function padTime(value, fallback) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
  if (!match) return fallback;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return fallback;
  return String(hour).padStart(2, '0') + ':' + match[2];
}

function defaultDays() {
  const days = {};
  DAYS.forEach(([key]) => {
    const open = key !== 'sun' && key !== 'sat';
    days[key] = { open, start: '09:00', end: '17:00' };
  });
  return days;
}

function daysFromWeekly(weekly) {
  const days = defaultDays();
  DAYS.forEach(([key]) => { days[key] = { open: false, start: '09:00', end: '17:00' }; });
  if (!weekly || typeof weekly !== 'object') return days;
  DAYS.forEach(([key]) => {
    const row = weekly[key] || weekly[key.toUpperCase()];
    if (!row || row.closed === true || row.open === false) return;
    const start = row.start || row.open;
    const end = row.end || row.close;
    if (!start || !end) return;
    days[key] = { open: true, start: padTime(start, '09:00'), end: padTime(end, '17:00') };
  });
  return days;
}

function holidaysFromConfig(list) {
  if (!Array.isArray(list)) return [];
  return list.map((entry) => {
    if (entry && typeof entry === 'object') {
      return { date: String(entry.date || entry.day || '').slice(0, 10), name: String(entry.name || entry.label || '') };
    }
    return { date: String(entry || '').slice(0, 10), name: '' };
  }).filter((entry) => /^\d{4}-\d{2}-\d{2}$/.test(entry.date));
}

export function BusinessHoursSettings({ api, toast, timezone }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [limitHours, setLimitHours] = useState(false);
  const [days, setDays] = useState(defaultDays);
  const [holidays, setHolidays] = useState([]);
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [afterHours, setAfterHours] = useState('voicemail');
  const [zone, setZone] = useState(timezone || '');

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await api('GET', '/api/tenant/settings');
        if (cancel) return;
        const hours = data && data.business_hours_json;
        const weekly = hours && (hours.weekly || hours.hours);
        setLimitHours(!!(weekly && typeof weekly === 'object'));
        setDays(weekly ? daysFromWeekly(weekly) : defaultDays());
        setHolidays(holidaysFromConfig(hours && hours.holidays));
        const behavior = data && data.after_hours_behavior;
        setAfterHours(behavior === 'callback' || behavior === 'message' ? behavior : 'voicemail');
        setZone((data && data.timezone) || timezone || '');
      } catch (err) {
        if (!cancel) setError(errorMessage(err, 'Could not load business hours.'));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api, timezone]);

  function patchDay(key, patch) {
    setDays((current) => Object.assign({}, current, { [key]: Object.assign({}, current[key], patch) }));
  }

  function copyMonday() {
    setDays((current) => {
      const next = Object.assign({}, current);
      WEEKDAYS.forEach((key) => { next[key] = Object.assign({}, current.mon); });
      return next;
    });
  }

  function addHoliday(event) {
    event.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(holidayDate)) {
      toast('Choose a closed date.', 'error');
      return;
    }
    if (holidays.some((entry) => entry.date === holidayDate)) {
      toast('That date is already closed.', 'error');
      return;
    }
    setHolidays(holidays.concat([{ date: holidayDate, name: holidayName.trim() }]));
    setHolidayDate('');
    setHolidayName('');
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      let businessHours = null;
      if (limitHours) {
        const weekly = {};
        DAYS.forEach(([key]) => {
          const row = days[key];
          if (row && row.open) weekly[key] = { start: padTime(row.start, '09:00'), end: padTime(row.end, '17:00') };
        });
        businessHours = {
          weekly,
          holidays: holidays.map((entry) => (entry.name ? { date: entry.date, name: entry.name } : entry.date))
        };
      }
      await api('PATCH', '/api/tenant/settings', {
        business_hours_json: businessHours,
        after_hours_behavior: afterHours
      });
      toast('Business hours saved.');
    } catch (err) {
      toast(errorMessage(err, 'Could not save business hours.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="cmp" onSubmit={save}>
      <div>
        <h2 className="cmp-title">Business hours</h2>
        <p className="cmp-lead">Workspace time zone: {zone || 'Not set'}.</p>
      </div>
      {error ? <div className="cmp-error">{error}</div> : null}
      {loading ? <p className="cmp-meta">Loading…</p> : null}
      <label className="cmp-check">
        <input type="checkbox" checked={limitHours} onChange={(e) => setLimitHours(e.target.checked)} />
        {' '}Only answer during these hours
      </label>
      {!limitHours ? (
        <p className="cmp-note">The AI answers whenever someone calls.</p>
      ) : (
        <div className="cmp-card cmp-stack">
          <div className="cmp-hours-actions">
            <button type="button" className="cmp-btn" onClick={copyMonday}>Copy Monday to weekdays</button>
          </div>
          <div className="cmp-hours">
            {DAYS.map(([key, label]) => (
              <div className="cmp-hours-row" key={key}>
                <strong>{label}</strong>
                <label className="cmp-check">
                  <input
                    type="checkbox"
                    checked={!!days[key].open}
                    onChange={(e) => patchDay(key, { open: e.target.checked })}
                  />
                  {' '}Open
                </label>
                <label className="cmp-field">
                  Opens
                  <input className="cmp-input" type="time" value={days[key].start} disabled={!days[key].open} onChange={(e) => patchDay(key, { start: e.target.value })} />
                </label>
                <label className="cmp-field">
                  Closes
                  <input className="cmp-input" type="time" value={days[key].end} disabled={!days[key].open} onChange={(e) => patchDay(key, { end: e.target.value })} />
                </label>
              </div>
            ))}
          </div>
          <div>
            <h2>Closed dates</h2>
            <p className="cmp-help">These dates stay closed even when that weekday is open. They apply only while hours are limited.</p>
            {holidays.map((entry) => (
              <div className="cmp-holiday" key={entry.date}>
                <span>{entry.date}{entry.name ? ' — ' + entry.name : ''}</span>
                <button type="button" className="cmp-btn" onClick={() => setHolidays(holidays.filter((row) => row.date !== entry.date))}>Remove</button>
              </div>
            ))}
            <div className="cmp-holiday">
              <input className="cmp-input" type="date" value={holidayDate} onChange={(e) => setHolidayDate(e.target.value)} aria-label="Closed date" />
              <input className="cmp-input" value={holidayName} onChange={(e) => setHolidayName(e.target.value)} placeholder="Name (optional)" aria-label="Closed date name" />
              <button type="button" className="cmp-btn" onClick={addHoliday}>Add closed date</button>
            </div>
          </div>
          <label className="cmp-field">
            After hours
            <select className="cmp-select" value={afterHours} onChange={(e) => setAfterHours(e.target.value)}>
              {AFTER_HOURS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <p className="cmp-help">
            During open hours the AI answers. After you close, it can take a message, take a message and promise a callback, or just say you are closed.
            Calls are not forwarded to a phone number after hours.
          </p>
        </div>
      )}
      <div>
        <button type="submit" className="cmp-btn cmp-btn--primary" disabled={busy || loading}>Save business hours</button>
      </div>
    </form>
  );
}
